import { computeTiming } from '@fit-editor/fit-core'

import type { OperationPreviewState } from '@/hooks/use-operation-preview'
import { formatDurationSeconds } from '@/lib/format'

export interface OperationPreviewSummaryProps {
  state: OperationPreviewState
  /** True once the user has provided enough input to actually request a preview (e.g. both a start and end). */
  ready: boolean
}

/**
 * Concrete, numeric before/after for the pending operation -- record
 * count and elapsed-time delta -- plus any `OperationWarning`s the
 * engine would raise, all computed via `previewOperationAsync` and shown
 * before the user commits anything. Per the milestone brief: "a live
 * preview ... display the resulting record count / elapsed time delta --
 * something concrete, not just 'trust me'".
 */
export function OperationPreviewSummary({ state, ready }: OperationPreviewSummaryProps) {
  // `role="status"` (an implicit `aria-live="polite"` region) on every
  // non-error branch below -- without it, a screen-reader user who just
  // finished typing a date hears nothing when "Fill in the fields..."
  // silently becomes "Calculating preview…" and then the actual numbers,
  // since none of that text is otherwise announced. The error branch
  // uses the stronger `role="alert"` instead (already present before
  // this pass), which is the correct, more urgent choice for a failure.
  if (!ready) {
    return (
      <p role="status" className="text-muted-foreground text-xs">
        Fill in the fields above to see a preview.
      </p>
    )
  }

  if (state.error != null) {
    return (
      <div
        role="alert"
        className="border-destructive/50 bg-destructive/10 flex flex-col gap-1 rounded-md border p-2 text-xs"
      >
        <p className="text-destructive font-medium">Could not compute a preview.</p>
        <p className="text-destructive">{state.error}</p>
      </div>
    )
  }

  if (state.loading || state.baseline == null || state.preview == null) {
    return (
      <p role="status" className="text-muted-foreground text-xs">
        Calculating preview…
      </p>
    )
  }

  const beforeCount = state.baseline.activity.records.length
  const afterCount = state.preview.activity.records.length
  const beforeTiming = computeTiming(state.baseline.activity)
  const afterTiming = computeTiming(state.preview.activity)

  return (
    <div
      role="status"
      className="border-border bg-background flex flex-col gap-1 rounded-md border p-2 text-xs"
    >
      <p className="text-foreground font-medium">Preview</p>
      <p className="text-muted-foreground">
        Records: {beforeCount} &rarr; {afterCount}
        {afterCount !== beforeCount &&
          ` (${afterCount > beforeCount ? '+' : ''}${afterCount - beforeCount})`}
      </p>
      <p className="text-muted-foreground">
        Elapsed time: {formatDurationSeconds(beforeTiming.elapsedTimeSec)} &rarr;{' '}
        {formatDurationSeconds(afterTiming.elapsedTimeSec)}
      </p>
      {state.preview.warnings.length > state.baseline.warnings.length && (
        <p className="text-warning-text bg-warning/20 mt-1 rounded-sm p-1.5">
          {state.preview.warnings
            .slice(state.baseline.warnings.length)
            .map((w) => w.message)
            .join(' ')}
        </p>
      )}
    </div>
  )
}
